import React, { useMemo } from 'react';
import cn from 'classnames';
import Slider, { SliderTooltip } from 'rc-slider';
import { observer, calculatorState as store } from 'store';
import { useRangeStepsState } from '../../../hooks';
import { getDefaultBudgetMarks, getDefaultTicketsMarks, getDefaultDurationMarks } from './utils';
import { useTrans } from 'utils/localisation';
import 'rc-slider/assets/index.css';
import * as styles from './RangeSlider.module.scss';
import './RangeSlider.scss';

const { Handle } = Slider;

function Desktop({
  calculatorID,
  sectionName,
  onChangeCount,
  defaultValue,
  type,
  initialStep = 1,
}) {
  const [step, setStep] = useRangeStepsState(type, initialStep, calculatorID);
  const t = useTrans(['calculator', 'common']);

  const getDuration = monthCount => {
    switch (monthCount) {
      case 1:
        return `${monthCount} ${t('month')}`;
      case 24: {
        return `${monthCount} ${t('months')}+`;
      }
      default: {
        return `${monthCount} ${t('months')}`;
      }
    }
  };

  const SliderInfoWindow = props => {
    const { value, dragging, index, ...restProps } = props;

    return (
      <SliderTooltip visible={dragging} placement="top" key={index}>
        <Handle value={value} {...restProps}>
          <div className={styles.tooltip}>
            <div className={styles.tooltipValue}>{getDuration(value)}</div>
          </div>
        </Handle>
      </SliderTooltip>
    );
  };

  const sliderProps = useMemo(() => {
    switch (type) {
      case 'tickets':
        return {
          onChange: value => {
            const _value = Math.min(5, Math.max(1, value));
            const tickets = getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[
              _value
            ].includes('tickets')
              ? getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[_value]
              : `${getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[_value]} ${t(
                  'tickets',
                )}`;

            setStep(_value);
            store.clearSectionInfo(calculatorID, sectionName, defaultValue);
            onChangeCount(tickets, _value);
          },
          min: 0,
          max: 6,
          marks: getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over')),
          step: null,
          value: step,
          defaultValue: 1,
        };
      case 'budget':
        return {
          onChange: value => {
            const _value = Math.min(5, Math.max(1, value));

            setStep(_value);
            store.clearSectionInfo(calculatorID, sectionName, defaultValue);
            onChangeCount(getDefaultBudgetMarks(t('upTo'), t('k'), t('over'))[_value], _value);
          },
          min: 0,
          max: 6,
          marks: getDefaultBudgetMarks(t('upTo'), t('k'), t('over')),
          step: null,
          value: step,
          defaultValue: 1,
        };
      case 'duration':
        return {
          onChange: value => {
            const _value = Math.min(24, Math.max(1, value));

            setStep(_value);
            store.clearSectionInfo(calculatorID, sectionName, defaultValue);
            onChangeCount(getDuration(_value), _value);
          },
          min: 0,
          max: 25,
          marks: getDefaultDurationMarks(t('month'), t('months'), t('year'), t('years')),
          step: 1,
          value: step,
          defaultValue: 1,
          handle: SliderInfoWindow,
        };
      default:
        return null;
    }
  }, [type, step]);

  return (
    <div className={cn(styles.container, `calculator-slider-${type}`, styles[type])}>
      <Slider className={styles.slider} {...sliderProps} />
    </div>
  );
}

export default observer(Desktop);
